import type { FieldErrors, FieldValues, Resolver } from 'react-hook-form';
import type { z } from 'zod';

/**
 * Connects a Zod schema to React Hook Form without the @hookform/resolvers package: one
 * small function instead of a dependency and its version coupling. Reports only the first
 * issue per field, so a user sees one actionable message rather than a list.
 */
export function zodResolver<F extends FieldValues>(schema: z.ZodType): Resolver<F> {
  return async (values) => {
    const result = schema.safeParse(values);
    if (result.success) {
      return { values: result.data as F, errors: {} };
    }
    const errors: Record<string, { type: string; message: string }> = {};
    for (const issue of result.error.issues) {
      const key = issue.path.join('.') || 'root';
      errors[key] ??= { type: issue.code, message: issue.message };
    }
    return { values: {}, errors: errors as FieldErrors<F> };
  };
}
